import { useEffect, useRef, useState } from "react";
import api from "../lib/api";
import { toast } from "sonner";
import { Image as ImageIcon, Upload, Trash2, X as XIcon } from "lucide-react";
import ConfirmDialog from "./ConfirmDialog";

/**
 * Establishment logo modal — opens from the "LOGO" header shortcut on the
 * merchant dashboard. Same upload/remove behaviour as the previous inline
 * panel (POST /merchant/me/logo, DELETE /merchant/me/logo). The logo is
 * rendered in the public display header behind the establishment name.
 */
export default function CommerceLogoDialog({ open, commerce, onClose, onChange }) {
  const [hasLogo, setHasLogo] = useState(!!commerce?.hasLogo);
  const [logoUrl, setLogoUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  const [removeConfirm, setRemoveConfirm] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    setHasLogo(!!commerce?.hasLogo);
    if (commerce?.id) {
      const apiBase = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "") + "/api";
      const cb = encodeURIComponent(commerce.logoUpdatedAt || commerce.id);
      setLogoUrl(commerce.hasLogo ? `${apiBase}/commerces/${commerce.id}/logo?v=${cb}` : null);
    }
  }, [commerce?.id, commerce?.hasLogo, commerce?.logoUpdatedAt]);

  if (!open) return null;

  const onPick = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    if (!["image/jpeg", "image/jpg", "image/png"].includes(f.type)) {
      toast.error("Format non supporté (JPEG, JPG ou PNG uniquement)");
      return;
    }
    if (f.size > 6 * 1024 * 1024) {
      toast.error("Image trop volumineuse (6 Mo max)");
      return;
    }
    upload(f);
  };

  const upload = async (file) => {
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post(`/merchant/me/logo`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Logo établissement importé");
      onChange && onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Échec de l'import");
    }
    setBusy(false);
  };

  const performRemove = async () => {
    setRemoveConfirm(false);
    try {
      await api.delete(`/merchant/me/logo`);
      toast.success("Logo supprimé");
      onChange && onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-ink/70 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="commerce-logo-dialog"
    >
      <div
        className="relative w-full max-w-lg bg-white rounded-2xl border border-border shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="px-5 py-3 border-b border-border flex items-center justify-between">
          <h2 className="font-display font-black uppercase tracking-[0.06em] text-base text-ink inline-flex items-center gap-2">
            <ImageIcon size={16} className="text-brand" /> Logo de l'Établissement
          </h2>
          <button
            onClick={onClose}
            className="text-subtle hover:text-ink"
            aria-label="Fermer"
            data-testid="commerce-logo-dialog-close"
          >
            <XIcon size={18} />
          </button>
        </header>
        <div className="p-5">
          <div className="flex items-center gap-4 flex-wrap">
            <div className="shrink-0 w-24 h-24 border border-border bg-white rounded-md flex items-center justify-center overflow-hidden" data-testid="commerce-logo-dialog-preview">
              {hasLogo && logoUrl ? (
                <img src={logoUrl} alt="Logo établissement" className="max-w-full max-h-full object-contain" />
              ) : (
                <div className="text-subtle text-[10px] text-center px-1">
                  <ImageIcon size={20} className="mx-auto mb-1" />
                  Aucun logo
                </div>
              )}
            </div>
            <p className="flex-1 min-w-[180px] text-xs text-subtle leading-relaxed">
              Affiché en haut de l'écran public, sous le nom de l'Établissement. Formats : <strong>JPEG, JPG, PNG</strong> · <strong>6 Mo max.</strong>
            </p>
          </div>

          <input
            ref={inputRef}
            type="file"
            accept="image/jpeg,image/jpg,image/png"
            onChange={onPick}
            className="hidden"
            data-testid="commerce-logo-dialog-file-input"
          />
          <div className="mt-5 flex flex-wrap gap-2 justify-end">
            {hasLogo && (
              <button
                onClick={() => setRemoveConfirm(true)}
                className="inline-flex items-center gap-1.5 h-10 px-3 rounded-md border border-destructive/40 text-destructive hover:bg-destructive hover:text-white text-xs font-bold uppercase tracking-wider transition-colors"
                data-testid="commerce-logo-dialog-remove-btn"
              >
                <Trash2 size={12} /> Supprimer
              </button>
            )}
            <button
              onClick={() => inputRef.current?.click()}
              disabled={busy}
              className="inline-flex items-center gap-1.5 h-10 px-4 bg-brand text-white text-xs font-bold uppercase tracking-wider rounded-md hover:bg-brand-hover disabled:opacity-50"
              data-testid="commerce-logo-dialog-upload-btn"
            >
              <Upload size={12} /> {busy ? "Import…" : (hasLogo ? "Remplacer le logo" : "Importer un logo")}
            </button>
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={removeConfirm}
        title="Supprimer le logo ?"
        message="Le logo de l'Établissement sera retiré de l'écran public et de toutes les vues visiteur."
        confirmLabel="Supprimer"
        destructive
        onConfirm={performRemove}
        onCancel={() => setRemoveConfirm(false)}
        testid="commerce-logo-dialog-remove-confirm"
      />
    </div>
  );
}
